import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { FaPlay, FaPause } from 'react-icons/fa';
import './AudioPlayer.css';

const AudioPlayer = ({ audioUrl }) => {
  const waveformRef = useRef(null);
  const [waveform, setWaveform] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [duration, setDuration] = useState('00:30');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (waveformRef.current && audioUrl) {
      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#A0D9FF',
        progressColor: '#5494DA',
        cursorWidth: 0,
        barWidth: 2,
        barGap: 2,
        height: 60,
        barRadius: 3,
        normalize: true,
        responsive: true,
        fillParent: true,
        autoScroll: false,
        hideScrollbar: true,
        minPxPerSec: 1, // Adjust this to fit the entire waveform
        backend: 'WebAudio',
        xhr: {
          // Disable range requests to load full audio at once
          mode: 'no-cors'
        }
      });

      wavesurfer.load(audioUrl);

      wavesurfer.on('ready', () => {
        setWaveform(wavesurfer);
        setIsLoading(false);
        setDuration(formatTime(wavesurfer.getDuration()));
      });

      wavesurfer.on('audioprocess', () => {
        setCurrentTime(formatTime(wavesurfer.getCurrentTime()));
      });

      wavesurfer.on('play', () => setIsPlaying(true));
      wavesurfer.on('pause', () => setIsPlaying(false));
      wavesurfer.on('finish', () => {
        setIsPlaying(false);
        setCurrentTime(formatTime(0));
      });

      wavesurfer.on('error', (err) => {
        console.error('WaveSurfer error:', err);
        setIsLoading(false);
      });

      return () => {
        wavesurfer.destroy();
      };
    }
  }, [audioUrl]);

  const formatTime = (timeInSeconds) => {
    if (!timeInSeconds && timeInSeconds !== 0) return '00:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (waveform && !isLoading) {
      waveform.playPause();
    }
  };

  return (
    <div className="audio-player-container">
      <button 
        className={`play-button ${isPlaying ? 'playing' : ''}`}
        onClick={handlePlayPause}
        disabled={isLoading}
      >
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>
      <div className="waveform-container">
        <div ref={waveformRef} className="waveform" />
        <div className="time-display">
          <span className="current-time">{currentTime}</span>
          <span className="duration">{duration}</span>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;